import { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Stack, FormControlLabel, Switch,
} from '@mui/material';
import intl from 'components/i18n/ReactIntlWrapper';
import ResizableDrawer from 'components/Layout/ResizableDrawer';
import RichTextEditor from 'components/common/RichTextEditor';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  item: any;
}

const WorkExperienceDrawer: React.FC<Props> = ({ open, onClose, onSave, item }) => {
  const [form, setForm] = useState({
    companyName: '', role: '', startDate: '', endDate: '', description: '',
  });
  const [isCurrent, setIsCurrent] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      companyName: item?.companyName ?? '',
      role: item?.role ?? '',
      startDate: item?.startDate ? item.startDate.split('T')[0] : '',
      endDate: item?.endDate ? item.endDate.split('T')[0] : '',
      description: item?.description ?? '',
    });
    setIsCurrent(!item?.endDate);
  }, [item, open]);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.companyName.trim() || !form.role.trim() || !form.startDate) return;
    setSaving(true);
    try {
      await onSave({
        companyName: form.companyName.trim(),
        role: form.role.trim(),
        startDate: form.startDate,
        endDate: isCurrent ? null : form.endDate || null,
        description: (form.description && form.description !== '<p></p>') ? form.description : null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResizableDrawer open={open} onClose={onClose} defaultWidth={440}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {item ? intl.get('hr.workExp.edit') : intl.get('hr.workExp.add')}
      </Typography>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <TextField label={intl.get('hr.workExp.company')} value={form.companyName} onChange={set('companyName')} fullWidth required sx={{ mb: 2 }} />
        <TextField label={intl.get('hr.workExp.role')} value={form.role} onChange={set('role')} fullWidth required sx={{ mb: 2 }} />
        <TextField label={intl.get('hr.workExp.startDate')} value={form.startDate} onChange={set('startDate')} fullWidth required type="date" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
        <FormControlLabel
          control={<Switch checked={isCurrent} onChange={(e) => setIsCurrent(e.target.checked)} />}
          label={intl.get('hr.workExp.currentJob')}
          sx={{ mb: 1 }}
        />
        {!isCurrent && (
          <TextField label={intl.get('hr.workExp.endDate')} value={form.endDate} onChange={set('endDate')} fullWidth type="date" InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
        )}
        <Box sx={{ mb: 3 }}>
          <RichTextEditor
            label={intl.get('hr.workExp.description')}
            value={form.description}
            onChange={(html) => setForm((f) => ({ ...f, description: html }))}
            minHeight={140}
          />
        </Box>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose}>{intl.get('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={saving}>{intl.get('common.save')}</Button>
        </Stack>
      </Box>
    </ResizableDrawer>
  );
};

export default WorkExperienceDrawer;
